import React, { useCallback } from 'react';

import { useTranslation } from 'react-i18next';
import { Edge, SafeAreaView } from 'react-native-safe-area-context';
import { useSelector } from 'react-redux';

import { useNavigation } from '@react-navigation/core';
import { StackNavigationProp } from '@react-navigation/stack';

import { LANGUAGE_KEYS } from 'config/languageKeys';

import { SCREEN } from 'router/screens.ts';
import { RouteNavigationParams } from 'router/types.ts';

import { selectLayoutConfig } from 'store/config/selectors';

import { COLOR } from 'utils/colors';
import isFunction from 'utils/function.ts';
import useSafeAreaEdges from 'utils/hooks/useSafeAreaEdges';
import { isTablet } from 'utils/responsiveness';

import DialogView from 'components/Dialog';
import DialogBody from 'components/Dialog/components/DialogBody';

import useConnectionIndicator from './hooks/index.tsx';
import styles from './styles.ts';

const {
    CONNECTION_INDICATOR: {
        NOTIFICATION_RN_INTERNET_CONNECTION_USE_MOBILE_DATA_TITLE,
        NOTIFICATION_RN_INTERNET_CONNECTION_USE_MOBILE_DATA_DESCRIPTION,
        BUTTON_IGNORE,
        BUTTON_GO_TO_SETTINGS,
    },
} = LANGUAGE_KEYS;

export default function ConnectionIndicatorAlert({
    isPlayerNotification,
    isModalNotification,
    supportedOrientation,
    handleClosePlayer,
}: {
    isModalNotification?: boolean;
    isPlayerNotification?: boolean;
    supportedOrientation?: Array<
        | 'portrait'
        | 'portrait-upside-down'
        | 'landscape'
        | 'landscape-left'
        | 'landscape-right'
    >;
    handleClosePlayer?: () => void;
}) {
    const { primary } = useSelector(selectLayoutConfig);
    const { t } = useTranslation();
    const edges: Edge[] = useSafeAreaEdges();

    const navigation =
        useNavigation<
            StackNavigationProp<
                RouteNavigationParams,
                SCREEN.SETTINGS_INTERNET_CONNECTION
            >
        >();

    const { showConnectionDialog, handleCloseDialog } =
        useConnectionIndicator();

    const handleRedirectToSettings = useCallback(() => {
        if (isFunction(handleClosePlayer)) {
            handleClosePlayer();
        }

        setTimeout(() => {
            handleCloseDialog();
        }, 0);
        navigation.navigate(SCREEN.SETTINGS_INTERNET_CONNECTION);
    }, [navigation, handleClosePlayer]);

    const handleIgnoreButton = useCallback(() => {
        if (isFunction(handleClosePlayer)) {
            handleClosePlayer();
        }

        setTimeout(() => {
            handleCloseDialog();
        }, 0);
    }, [handleCloseDialog, handleClosePlayer]);

    const title = t(NOTIFICATION_RN_INTERNET_CONNECTION_USE_MOBILE_DATA_TITLE);

    if (
        (showConnectionDialog && isModalNotification) ||
        (isPlayerNotification && showConnectionDialog)
    ) {
        return (
            <SafeAreaView style={styles.notificationContainer} edges={edges}>
                <DialogBody
                    title={title.replace(/\r?\n/g, '')}
                    titleTestID={
                        NOTIFICATION_RN_INTERNET_CONNECTION_USE_MOBILE_DATA_TITLE
                    }
                    description={t(
                        NOTIFICATION_RN_INTERNET_CONNECTION_USE_MOBILE_DATA_DESCRIPTION,
                    )}
                    descriptionTestID={
                        NOTIFICATION_RN_INTERNET_CONNECTION_USE_MOBILE_DATA_DESCRIPTION
                    }
                    actionButtonTitle={t(BUTTON_GO_TO_SETTINGS)}
                    handleActionButtonPress={handleRedirectToSettings}
                    actionButtonFillColor={primary || COLOR.primary}
                    actionButtonStyle={styles.button}
                    actionButtonTextStyle={styles.buttonText}
                    actionButtonTestID={BUTTON_GO_TO_SETTINGS}
                    cancelButtonTitle={t(BUTTON_IGNORE)}
                    cancelButtonBordered
                    handleCancelPress={handleIgnoreButton}
                    onRequestClose={handleIgnoreButton}
                />
            </SafeAreaView>
        );
    }

    if (!isPlayerNotification && showConnectionDialog) {
        return (
            <SafeAreaView edges={edges}>
                <DialogView
                    visible={showConnectionDialog}
                    title={isTablet ? title.replace(/\r?\n/g, '') : title}
                    titleTestID={
                        NOTIFICATION_RN_INTERNET_CONNECTION_USE_MOBILE_DATA_TITLE
                    }
                    description={t(
                        NOTIFICATION_RN_INTERNET_CONNECTION_USE_MOBILE_DATA_DESCRIPTION,
                    )}
                    actionButtonTitle={t(BUTTON_GO_TO_SETTINGS)}
                    descriptionTestID={
                        NOTIFICATION_RN_INTERNET_CONNECTION_USE_MOBILE_DATA_DESCRIPTION
                    }
                    actionButtonOnPress={handleRedirectToSettings}
                    actionButtonFillColor={primary || COLOR.primary}
                    actionButtonStyle={styles.button}
                    actionButtonTextStyle={styles.buttonText}
                    supportedOrientation={supportedOrientation}
                    actionButtonTestID={BUTTON_GO_TO_SETTINGS}
                    cancelButtonBordered
                    cancelButtonTitle={t(BUTTON_IGNORE)}
                    cancelButtonOnPress={handleIgnoreButton}
                />
            </SafeAreaView>
        );
    }

    return null;
}
